import { useRouterState } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useState } from "react";
import { encodeOrigin, type OriginDescriptor } from "@/lib/detail-origin";

/**
 * Universal "where am I, so a page I link to can send you back here" context.
 *
 * Any contextual in-app link can call {@link useOriginLinkSearch} to stamp the
 * CURRENT page as the origin of wherever it navigates. The target page then
 * shows a breadcrumb + "← Back to X" that returns to exactly here. The label is
 * resolved from {@link ROUTE_LABELS} by the current route, unless the page
 * overrides it with a record name via {@link useSetOrigin} (e.g. a person's
 * profile publishes their name).
 *
 * Single-level by design: the captured origin strips any inbound `origin`/`lf`
 * from the current search, so origins never nest and the URL stays bounded.
 */

/** Human labels for each navigable route, keyed by its templated route id. */
const ROUTE_LABELS: Record<string, string> = {
  "/dashboard/my": "My Dashboard",
  "/dashboard/my/certifications": "My Certifications",
  "/dashboard/persons": "People",
  "/dashboard/persons/$personId": "Person",
  "/dashboard/organization": "Organization",
  "/dashboard/organization/chart": "Org Chart",
  "/dashboard/roles": "Roles",
  "/dashboard/roles/compare": "Compare Roles",
  "/dashboard/roles/$roleId": "Role",
  "/dashboard/skills": "Skills",
  "/dashboard/skills/$skillId": "Skill",
  "/dashboard/competencies": "Competencies",
  "/dashboard/competencies/$competencyId": "Competency",
  "/dashboard/certifications": "Certifications",
  "/dashboard/certifications/not-started": "Certs Not Started",
  "/dashboard/certifications/$certificationId": "Certification",
  "/dashboard/capability-gaps": "Capabilities",
  "/dashboard/constraints": "Constraints",
  "/dashboard/reviews": "Reviews",
  "/dashboard/reviews/rounds/$cycleId": "Review Round",
  "/dashboard/reviews/tasks/$reviewerId": "Review",
  "/dashboard/reviews/reports/$reviewId": "Review Report",
  "/dashboard/reviews/forms": "Review Forms",
  "/dashboard/reviews/forms/$templateId": "Review Form",
  "/dashboard/org-health": "Org Health",
  "/dashboard/aspirations": "Role Aspirations",
  "/dashboard/whats-new": "What's New",
  "/dashboard/help": "Help",
};

// A page-set label override (e.g. a record name). Two contexts so the setter is
// referentially stable and only re-renders consumers when the label changes —
// mirrors breadcrumbs.tsx.
const OverrideLabelContext = createContext<string | null>(null);
const SetOverrideLabelContext = createContext<(label: string | null) => void>(() => {});

export function OriginProvider({ children }: { children: React.ReactNode }) {
  const [override, setOverride] = useState<string | null>(null);
  return (
    <SetOverrideLabelContext.Provider value={setOverride}>
      <OverrideLabelContext.Provider value={override}>{children}</OverrideLabelContext.Provider>
    </SetOverrideLabelContext.Provider>
  );
}

/**
 * Override the current page's origin label (e.g. a record name). Set on mount,
 * cleared on unmount. Pass the resolved name once it loads; it re-publishes.
 */
export function useSetOrigin(label: string | null | undefined): void {
  const setOverride = useContext(SetOverrideLabelContext);
  const value = label ?? null;
  useEffect(() => {
    setOverride(value);
    return () => setOverride(null);
  }, [setOverride, value]);
}

/**
 * Build the `search` a contextual `<Link>` (or `navigate`) passes so the page it
 * opens can return here. Reactive to the current location, so the captured
 * origin is always "the page this link is rendered on".
 */
export function useOriginLinkSearch(): { origin: string } {
  const override = useContext(OverrideLabelContext);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const routeId = useRouterState({ select: (s) => s.matches.at(-1)?.routeId ?? "" });

  const params = new URLSearchParams(searchStr);
  params.delete("origin");
  params.delete("lf");
  const search = Object.fromEntries(params);

  const descriptor: OriginDescriptor = {
    to: pathname,
    search: Object.keys(search).length > 0 ? search : undefined,
    label: override ?? ROUTE_LABELS[routeId] ?? "Back",
  };
  return { origin: encodeOrigin(descriptor) };
}
